import {gql, useMutation} from '@apollo/client';
import {useEffect, useRef} from 'react';
import {Pda} from './components/Pda';
import {Header} from './components/Header';
import { ArrivalRelatedScanComponent, HandleScanEventResult } from './components/ArrivalRelatedScanComponent';
import { showTrackingNumberError } from '../../utils/BarCodeUtil';
import { isTrackingNumber } from '../../utils/BarCodeUtil';
import { t } from '../../i18n';

const HANDLE_DESTROY_SCAN_EVENT = gql`
   mutation HandleWhsDestroyScanEvent($barCode: String!) {
     success: handleWhsDestroyScanEvent(barCode: $barCode)
   }
`

const DEFAULT_BORDER_COLOR = 'transparent'; // 默认透明
const SUCCESS_BORDER_COLOR = 'rgb(34, 197, 94)'; // green-500

const PageBody = ({onScanStart, onScanSuccess}: {onScanStart: () => void; onScanSuccess: () => void}) => {
    const [handleDestroyScanEvent] = useMutation<HandleScanEventResult>(HANDLE_DESTROY_SCAN_EVENT);

    const handleScanEvent = async (barCode: string): Promise<HandleScanEventResult> => {
        // 新扫描开始时，先把边框恢复为默认色（不触发 React 重渲染）
        onScanStart();

        if (!isTrackingNumber(barCode)) {
            showTrackingNumberError(barCode);
            return {success: false};
        }

        const {data} = await handleDestroyScanEvent({
            variables: {
                barCode
            }
        });
        const success = data?.success || false;
        if (success) {
            onScanSuccess();
        }
        return {success};
    };

    return (
        <ArrivalRelatedScanComponent
            value1Placeholder={t('pda.pages.destroy.trackingNO')}
            value1ColumnTitle={t('pda.pages.destroy.trackingNO')}
            handleScanEvent={handleScanEvent}
        />
    );
}

export default function DestroyPage() {
    const borderElRef = useRef<HTMLDivElement | null>(null);
    const borderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setBorderColor = (color: string) => {
        const el = borderElRef.current;
        if (!el) return;
        el.style.outlineColor = color;
    };

    const clearBorderTimeout = () => {
        if (borderTimeoutRef.current) {
            clearTimeout(borderTimeoutRef.current);
            borderTimeoutRef.current = null;
        }
    };

    const handleScanStart = () => {
        clearBorderTimeout();
        setBorderColor(DEFAULT_BORDER_COLOR);
    };

    const handleScanSuccess = () => {
        clearBorderTimeout();
        setBorderColor(SUCCESS_BORDER_COLOR);
        borderTimeoutRef.current = setTimeout(() => {
            setBorderColor(DEFAULT_BORDER_COLOR);
            borderTimeoutRef.current = null;
        }, 1500);
    };

    // 仅在页面挂载时创建一次全屏边框 DOM；后续只改样式，避免触发 video 重载/闪烁
    useEffect(() => {
        const el = document.createElement('div');
        el.setAttribute('data-pda-destroy-success-border', '1');
        el.style.position = 'fixed';
        el.style.inset = '0';
        el.style.pointerEvents = 'none';
        el.style.zIndex = '9999';
        el.style.outlineStyle = 'solid';
        el.style.outlineWidth = '6px';
        el.style.outlineColor = DEFAULT_BORDER_COLOR;
        el.style.outlineOffset = '-6px';
        el.style.borderRadius = '8px';
        el.style.transition = 'outline-color 150ms ease';

        document.body.appendChild(el);
        borderElRef.current = el;

        return () => {
            clearBorderTimeout();
            borderElRef.current = null;
            el.remove();
        };
    }, []);

    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            <Header title={t('pda.pages.destroy.destroyScan')}/>
            <Pda/>
            <PageBody onScanStart={handleScanStart} onScanSuccess={handleScanSuccess}/>
        </div>
    );
}
